var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React from "react";
import { Box, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import TemperatureDisplay from "@ui/components/temperature/TemperatureDisplay";
import { GridCellStack } from "@ui/components/grid";
import { SButton } from "@ui/components/buttons/SButton";
import { getNodeTemperatureColor, getNodeTemperatureStatus, } from "@ui/components/temperature";
import { SMenu } from "@ui/components/menu/SMenu";
import { PermissionChecker, Permission } from "@utility/permission-checker";
import { formatUtcToLocalDate, formatUtcToLocalTime, } from "@application/common/helpers/date-util";
import { SensorAlarmMarker } from "./SensorAlarmMarker";
import { SensorReading } from "./SensorReading";
import { SensorType, isSensorHidden } from "@ui/utils/sensors";
import { limitReasons, worstRowStatus } from "@ui/utils/sensor-limit-status";
import { CardMenuButton, DeviceDetailsCard, } from "@ui/components/card/DeviceDetailsCard";
import { DeviceDetailsRow, DeviceStatusIndicator, TemperatureStatusChip, } from "@ui/components/grid/DeviceDetailsRow";
/** Width of a reading tile's column; the AP row and the node rows share it so their tiles line up. */
export const READING_COLUMN_WIDTH = 123;
const SectionLabel = ({ children, mb, }) => (React.createElement(Typography, { variant: "caption", component: "div", sx: { color: "grey.600", mb } }, children));
/** Opens the Edit Node page of the AP's own node (its id comes with the access point). */
const useOpenApInternalSettings = (apNodeId) => {
    const navigate = useNavigate();
    return () => {
        if (apNodeId)
            navigate(`/nodes/${apNodeId}/edit`);
    };
};
/**
 * Buttons shown on the AP's own row. Settings opens the node's Edit page and Logs opens the AP
 * node's log; Disconnect is visual only until the backend defines its behaviour.
 */
const APReadingActions = ({ deviceId, apNodeId }) => {
    const { t } = useTranslation("common");
    const navigate = useNavigate();
    const openSettings = useOpenApInternalSettings(apNodeId);
    return (React.createElement(Box, { sx: { display: "flex", gap: 1 } },
        PermissionChecker.hasPermission(Permission.ACCESSPOINTS_DELETE) && (React.createElement(SButton, { variant: "soft", color: "error" }, t("actions.disconnect"))),
        React.createElement(SButton, { variant: "soft", color: "primary", disabled: !deviceId, onClick: (e) => {
                e.stopPropagation();
                // The AP node's uid is the AP's uid. No `apId` segment: the log page would then load the
                // AP's node dropdown, which never contains the AP node itself (it has no node connection),
                // and fall back to the first relayed node instead.
                navigate(`/nodes/${deviceId}/logs`);
            } }, t("actions.logs")),
        PermissionChecker.hasPermission(Permission.NODES_EDIT) && (React.createElement(SButton, { variant: "outlined", color: "primary", disabled: !apNodeId, onClick: openSettings }, t("actions.settings")))));
};
/**
 * Status/colour use the same rule as the node rows (getNodeTemperatureStatus): inactive without a temperature, otherwise
 * low / high against the limits of the AP node's CPU temperature, else normal. Without limits it is normal, as before.
 */
const apStatusInput = (isActive, temperature, lowTemperature, highTemperature) => ({
    nodeTemperature: temperature,
    lowTemperature,
    highTemperature,
    isActive: Boolean(isActive),
});
/** The AP's own sensor row, laid out on the same grid as the node rows. */
const APReadingsTableRow = ({ columns, isActive, deviceId, apNodeId, temperature, co2, humidity, lowTemperature, highTemperature, lastAlarms, sensorStatuses, disabledSensors, lastCommunicationAt, }) => {
    const { t } = useTranslation("common");
    const statusInput = apStatusInput(isActive, temperature, lowTemperature, highTemperature);
    const rowStatus = worstRowStatus(getNodeTemperatureStatus(statusInput), sensorStatuses);
    const nameStack = (React.createElement(GridCellStack, { topText: t("labels.apInternal"), bottomText: deviceId !== null && deviceId !== void 0 ? deviceId : "" }));
    const cells = {
        node: (React.createElement(TemperatureDisplay, { width: 91, temperature: temperature, color: getNodeTemperatureColor(statusInput) })),
        // Without alarms the cell is exactly what it was; with alarms the bell sits after the name, as on the node rows.
        nodeName: lastAlarms && lastAlarms.length > 0 ? (React.createElement(Box, { sx: { display: "flex", alignItems: "center", gap: 0.75, height: "100%", width: "100%", minWidth: 0 } },
            React.createElement(Box, { sx: { minWidth: 0, flex: 1 } }, nameStack),
            React.createElement(SensorAlarmMarker, { alarms: lastAlarms }))) : (nameStack),
        // A sensor switched off on the Edit Node page leaves its cell empty.
        primaryReading: isSensorHidden(disabledSensors, SensorType.Co2) ? null : (React.createElement(SensorReading, { value: co2, unit: "ppm", tone: "co2", inactive: !isActive, sensorType: SensorType.Co2, sensorLabel: t("labels.co2"), statuses: sensorStatuses })),
        humidity: isSensorHidden(disabledSensors, SensorType.Humidity) ? null : (React.createElement(SensorReading, { value: humidity, unit: "%", tone: "humidity", inactive: !isActive, sensorType: SensorType.Humidity, sensorLabel: t("labels.humidity"), statuses: sensorStatuses })),
        temperature: (React.createElement(TemperatureStatusChip, { status: rowStatus, reason: limitReasons(sensorStatuses, t) })),
        status: React.createElement(DeviceStatusIndicator, { active: Boolean(isActive) }),
        lastUpdate: (React.createElement(GridCellStack, { topText: formatUtcToLocalDate(lastCommunicationAt), bottomText: formatUtcToLocalTime(lastCommunicationAt) })),
        actions: React.createElement(APReadingActions, { deviceId: deviceId, apNodeId: apNodeId }),
    };
    return (React.createElement(DeviceDetailsRow, { columns: columns, variant: "highlight", renderCell: (col) => { var _a; return (_a = cells[col.field]) !== null && _a !== void 0 ? _a : null; } }));
};
/** Card version of the AP's own sensor row, for the stacked (tablet / mobile) layout. */
const APReadingsCard = ({ isActive, deviceId, apNodeId, temperature, co2, humidity, lowTemperature, highTemperature, sensorStatuses, disabledSensors, lastCommunicationAt, }) => {
    const { t } = useTranslation("common");
    const statusInput = apStatusInput(isActive, temperature, lowTemperature, highTemperature);
    const rowStatus = worstRowStatus(getNodeTemperatureStatus(statusInput), sensorStatuses);
    const openSettings = useOpenApInternalSettings(apNodeId);
    const [anchorEl, setAnchorEl] = React.useState(null);
    const menuItems = PermissionChecker.hasPermission(Permission.NODES_EDIT) && apNodeId
        ? [{ label: t("actions.settings"), onClick: openSettings }]
        : [];
    return (React.createElement(DeviceDetailsCard, { tile: React.createElement(TemperatureDisplay, { width: 91, temperature: temperature, color: getNodeTemperatureColor(statusInput) }), name: t("labels.apInternal"), deviceId: deviceId !== null && deviceId !== void 0 ? deviceId : "", menu: menuItems.length > 0 ? (React.createElement(React.Fragment, null,
            React.createElement(CardMenuButton, { onClick: (e) => setAnchorEl(e.currentTarget) }),
            React.createElement(SMenu, { anchorEl: anchorEl, open: Boolean(anchorEl), handleClose: () => setAnchorEl(null), menuItems: menuItems }))) : (React.createElement(CardMenuButton, null)), 
        // The same tiles as the table row.
        readingTiles: [
            ...(isSensorHidden(disabledSensors, SensorType.Co2)
                ? []
                : [
                    React.createElement(SensorReading, { key: "co2", value: co2, unit: "ppm", tone: "co2", inactive: !isActive, sensorType: SensorType.Co2, sensorLabel: t("labels.co2"), statuses: sensorStatuses }),
                ]),
            ...(isSensorHidden(disabledSensors, SensorType.Humidity)
                ? []
                : [
                    React.createElement(SensorReading, { key: "humidity", value: humidity, unit: "%", tone: "humidity", inactive: !isActive, sensorType: SensorType.Humidity, sensorLabel: t("labels.humidity"), statuses: sensorStatuses }),
                ]),
        ], date: formatUtcToLocalDate(lastCommunicationAt), time: formatUtcToLocalTime(lastCommunicationAt), status: React.createElement(DeviceStatusIndicator, { active: Boolean(isActive) }), badge: React.createElement(TemperatureStatusChip, { status: rowStatus, size: "medium", reason: limitReasons(sensorStatuses, t) }) }));
};
/**
 * "AP Readings" block plus the "Externally Connected Nodes" heading that introduces the node
 * rows. Rendered above the node list on the Connected tab.
 */
export const APReadingsSection = (_a) => {
    var { cardView, columns } = _a, readings = __rest(_a, ["cardView", "columns"]);
    const { t } = useTranslation("common");
    return (React.createElement(React.Fragment, null,
        React.createElement(Box, { sx: { mb: 1 } },
            React.createElement(SectionLabel, { mb: "4px" }, t("labels.apReadings")),
            cardView ? (React.createElement(APReadingsCard, Object.assign({}, readings))) : (React.createElement(APReadingsTableRow, Object.assign({ columns: columns }, readings)))),
        React.createElement(SectionLabel, { mb: "4px" }, t("labels.connectedNodes"))));
};
